import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { APIRequestStatus } from 'common-ts/dist/models/APIRequestStatus';
import axios from 'axios';
import { fetchWorkBatchCutList } from "./WorkBatchCutListRedux";
import { fetchClothInventory } from "./MidstockListPostRedux";

interface WorkBatchCutPostState {
  dialogOpen: boolean
  dialogOpenUpdate: boolean

  updateDraft: any

  status: APIRequestStatus
  error: string
}

const initialState: WorkBatchCutPostState = {
    dialogOpen: false,
    dialogOpenUpdate: false,

    updateDraft: {},

    status: APIRequestStatus.Idle,
    error: ''
}

const workBatchCutPostSlice = createSlice({
    name: 'workBatchCutPost',
    initialState,
    reducers: {
        updateDialogOpen(state, action) {
            state.dialogOpen = action.payload;
        },
        updateDialogOpenUpdate(state, action) {
            state.dialogOpenUpdate = action.payload;
        },
        updateValUpdateDraft(state, action) {
            state.updateDraft = action.payload;
        }
    },
    extraReducers(builder) {
        builder
          .addCase(postNewWorkBatchCut.pending, (state, action) => {
            state.status = APIRequestStatus.RequestInProgress;
          })
          .addCase(postNewWorkBatchCut.fulfilled, (state, action) => {
            state.status = APIRequestStatus.Success;

            state.dialogOpen = false;
          })
          .addCase(postNewWorkBatchCut.rejected, (state, action) => {
            state.status = APIRequestStatus.Failure;
            console.log("fetch is failure");
          })
          .addCase(postUpdateWorkBatchCut.pending, (state, action) => {
            state.status = APIRequestStatus.RequestInProgress;
          })
          .addCase(postUpdateWorkBatchCut.fulfilled, (state, action) => {
            state.status = APIRequestStatus.Success;

            state.dialogOpenUpdate = false;
          })
          .addCase(postUpdateWorkBatchCut.rejected, (state, action) => {
            state.status = APIRequestStatus.Failure;
            console.log("fetch is failure");
          })
      }
})

export const postNewWorkBatchCut = createAsyncThunk('workBatchCutPost/postNewWorkBatchCut', async (arg: any, thunkAPI) => {
    const state: any = thunkAPI.getState();

    const response = await axios({
      method: 'post',
      url: 'https://8mav2hp6ki.execute-api.ap-southeast-1.amazonaws.com/OrderAPIProduction/work-batch-cut',
      data: arg
    });

    thunkAPI.dispatch(fetchWorkBatchCutList());

    return response.data
})

export const postUpdateWorkBatchCut = createAsyncThunk('workBatchCutPost/postUpdateWorkBatchCut', async (arg: any, thunkAPI) => {
    const state: any = thunkAPI.getState();

    const response = await axios({
      method: 'post',
      url: 'https://8mav2hp6ki.execute-api.ap-southeast-1.amazonaws.com/OrderAPIProduction/work-batch-cut',
      data: {
        ...arg,
      }
    });

    thunkAPI.dispatch(fetchWorkBatchCutList());

    return response.data
})

function dataURItoBlob(dataURI: string) {
    var binary = atob(dataURI.split(',')[1]);
    var array = [];
    for (var i = 0; i < binary.length; i++) {
        array.push(binary.charCodeAt(i));
    }
    return new Blob([new Uint8Array(array)], { type: 'image/jpeg' });
}

// Uploads each fabric's "after" photo, then COMPLETEs the cut with the fabric consumption
// list (clothInventoryId + weightAfter + photoAfterUrl). The backend derives consumed value
// and posts the fabric-waste journal entry.
export const postCompleteWorkBatchCutWithFabric = createAsyncThunk(
    'workBatchCutPost/postCompleteWithFabric',
    async (arg: { creationDateTime: string, fabricRows: any[] }, thunkAPI) => {
        const fabricConsumptionList: any[] = [];

        for (const row of arg.fabricRows) {
            let photoAfterUrl = '';
            if (row.fileContent) {
                const currentFileDraft = {
                    fileName: 'potong-after-' + row.clothInventoryId + '-' + Date.now() + '.jpg',
                    fileDataType: 'image/jpeg',
                    fileCategory: 'general',
                };
                const signed = await axios({
                    method: 'post',
                    url: 'https://w3q706jlua.execute-api.ap-southeast-1.amazonaws.com/FileSystemAPIProduction/signedurl',
                    data: currentFileDraft,
                });
                const uploadURL = signed.data.uploadURL;
                try {
                    await axios({ method: 'put', url: uploadURL, data: dataURItoBlob(row.fileContent), headers: { 'Content-Type': 'image/jpeg' } });
                } catch (e) {
                    console.log('fabric photo upload failed', e);
                }
                photoAfterUrl = uploadURL.split('?')[0];
            }
            fabricConsumptionList.push({
                clothInventoryId: row.clothInventoryId,
                weightAfter: Number(row.weightAfter) || 0,
                photoAfterUrl,
            });
        }

        const response = await axios({
            method: 'post',
            url: 'https://8mav2hp6ki.execute-api.ap-southeast-1.amazonaws.com/OrderAPIProduction/work-batch-cut',
            data: {
                processAction: 'COMPLETE',
                workType: 'BATCHCUT',
                creationDateTime: arg.creationDateTime,
                fabricConsumptionList,
            },
        });

        thunkAPI.dispatch(fetchWorkBatchCutList());
        // Fabric was consumed/locked — refresh Stok Kain so dropdowns aren't stale.
        thunkAPI.dispatch(fetchClothInventory());

        return response.data;
    }
);

// Supervisor approves a cut held in PENDING_APPROVAL (high waste). The password is validated
// server-side; the backend then commits the stored consumption and posts the journal entry.
export const approveWorkBatchCut = createAsyncThunk(
    'workBatchCutPost/approve',
    async (arg: { creationDateTime: string, approvalPassword: string, approvedBy?: string }, thunkAPI) => {
        const response = await axios({
            method: 'post',
            url: 'https://8mav2hp6ki.execute-api.ap-southeast-1.amazonaws.com/OrderAPIProduction/work-batch-cut',
            data: {
                processAction: 'APPROVE_CUT',
                workType: 'BATCHCUT',
                creationDateTime: arg.creationDateTime,
                approvalPassword: arg.approvalPassword,
                approvedBy: arg.approvedBy,
            },
        });
        thunkAPI.dispatch(fetchWorkBatchCutList());
        thunkAPI.dispatch(fetchClothInventory());
        return response.data;
    }
);

// Supervisor rejects a held cut: releases the fabric locks and returns it to DRAFTED.
export const rejectWorkBatchCut = createAsyncThunk(
    'workBatchCutPost/reject',
    async (arg: { creationDateTime: string, approvalPassword: string }, thunkAPI) => {
        const response = await axios({
            method: 'post',
            url: 'https://8mav2hp6ki.execute-api.ap-southeast-1.amazonaws.com/OrderAPIProduction/work-batch-cut',
            data: {
                processAction: 'REJECT_CUT',
                workType: 'BATCHCUT',
                creationDateTime: arg.creationDateTime,
                approvalPassword: arg.approvalPassword,
            },
        });
        thunkAPI.dispatch(fetchWorkBatchCutList());
        thunkAPI.dispatch(fetchClothInventory());
        return response.data;
    }
);

export const { updateDialogOpen, updateDialogOpenUpdate, updateValUpdateDraft } = workBatchCutPostSlice.actions

export default workBatchCutPostSlice.reducer